import { useEffect, useRef, useState } from 'react'

export interface PartSceneBundle {
  readonly mesh_files: {
    readonly part_geometry: { readonly file: string }
  }
  readonly business_files?: Record<string, { readonly file: string }>
  readonly texture_files?: Record<string, { readonly file: string; readonly metadata?: unknown }>
}

interface MountedViewer {
  readonly stopAnimation: () => void
}

/**
 * UID-311 — thin React wrapper around fictiv-3d-viewer's `ViewerClass`, a
 * vanilla three.js class (not a React component). Dynamically imported so
 * the ~1MB bundle (three.js included) stays out of the main modal chunk.
 *
 * fictiv-3d-viewer has no dispose/teardown API, and its InteractionController
 * attaches a `window` resize listener from inside the constructor with no
 * matching removal — every mount leaks one listener. Accepted for this demo
 * app (a bounded number of modal opens per session, no crash, no unbounded
 * growth) rather than forking/patching the third-party package; we at least
 * stop the render loop on unmount.
 */
export function PartViewer3D({ sceneBundle }: { readonly sceneBundle: PartSceneBundle }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')

  useEffect(() => {
    let viewer: MountedViewer | null = null
    let cancelled = false
    setStatus('loading')

    import('fictiv-3d-viewer')
      .then(({ ViewerClass }) => {
        if (cancelled || !containerRef.current) {
          return null
        }
        return ViewerClass.createFromPartSceneBundle(containerRef.current, sceneBundle)
      })
      .then((created) => {
        if (cancelled) {
          return
        }
        if (!created) {
          setStatus('error')
          return
        }
        viewer = created
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error')
        }
      })

    return () => {
      cancelled = true
      viewer?.stopAnimation()
    }
  }, [sceneBundle])

  return (
    <div className="part-config-viewer-3d" ref={containerRef}>
      {status === 'error' && (
        <p className="caption-text part-config-viewer-note">
          Preview unavailable — could not load the 3D model.
        </p>
      )}
    </div>
  )
}
